/**
 * Platform Vault Unlock Modal
 *
 * Platform-level modal for unlocking the credential vault with passphrase.
 * This version uses direct fetch calls instead of module-level hooks to avoid
 * cross-module dependencies.
 */

import { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

interface PlatformVaultUnlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Whether this is a first-time setup (no passphrase yet) or an unlock */
  isSetup: boolean;
  /** Called after successful unlock/setup */
  onSuccess?: () => void;
}

export function VaultUnlockModal({
  isOpen,
  onClose,
  isSetup,
  onSuccess,
}: PlatformVaultUnlockModalProps) {
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const queryClient = useQueryClient();

  const setupVault = async (passphrase: string) => {
    const response = await fetch('/api/v1/cvs/vault/setup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({ passphrase }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to setup vault');
    }

    return response.json();
  };

  const unlockVault = async (passphrase: string) => {
    const response = await fetch('/api/v1/cvs/vault/unlock', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({ passphrase, ttlSeconds: 28800 }), // 8 hours for post-login
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Invalid passphrase');
    }

    return response.json();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isSetup) {
      // Setting up vault for first time
      if (passphrase.length < 12) {
        setError('Passphrase must be at least 12 characters');
        return;
      }

      if (passphrase !== confirmPassphrase) {
        setError('Passphrases do not match');
        return;
      }

      setIsPending(true);
      try {
        await setupVault(passphrase);
        // After setup, unlock immediately
        await unlockVault(passphrase);

        // Invalidate vault status to refresh globally
        await queryClient.invalidateQueries({ queryKey: ['vault', 'status'] });

        setPassphrase('');
        setConfirmPassphrase('');
        onSuccess?.();
        onClose();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to setup vault');
      } finally {
        setIsPending(false);
      }
    } else {
      // Unlocking existing vault
      setIsPending(true);
      try {
        await unlockVault(passphrase);

        // Invalidate vault status to refresh globally
        await queryClient.invalidateQueries({ queryKey: ['vault', 'status'] });

        setPassphrase('');
        onSuccess?.();
        onClose();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Invalid passphrase');
      } finally {
        setIsPending(false);
      }
    }
  };

  return (
    <Transition show={isOpen}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="w-full max-w-md bg-[var(--grok-surface-1)] rounded-lg shadow-xl border border-[var(--grok-border)]">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[var(--ems-primary-muted)] rounded-lg">
                    <Lock size={24} className="text-[var(--ems-primary)]" />
                  </div>
                  <DialogTitle className="text-lg font-semibold text-[var(--grok-text-primary)]">
                    {isSetup ? 'Unlock Credential Vault' : 'Setup Credential Vault'}
                  </DialogTitle>
                </div>

                <p className="text-sm text-[var(--grok-text-secondary)] mb-6">
                  {isSetup
                    ? 'Enter your passphrase to unlock your credential vault for this session.'
                    : 'Create a passphrase to secure your credential vault. This passphrase encrypts all your stored credentials and is required to access them.'}
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="passphrase"
                        className="block text-sm font-medium text-[var(--grok-text-primary)] mb-1"
                      >
                        {isSetup ? 'Passphrase' : 'Create Passphrase'}
                      </label>
                      <div className="relative">
                        <input
                          id="passphrase"
                          type={showPassphrase ? 'text' : 'password'}
                          value={passphrase}
                          onChange={(e) => setPassphrase(e.target.value)}
                          placeholder={isSetup ? 'Enter your passphrase' : 'Minimum 12 characters'}
                          className="
                            w-full px-3 py-2 pr-10
                            bg-[var(--grok-surface-2)] border border-[var(--grok-border)]
                            rounded-lg text-[var(--grok-text-primary)]
                            placeholder:text-[var(--grok-text-muted)]
                            focus:outline-none focus:ring-2 focus:ring-[var(--ems-primary-ring)]
                          "
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassphrase(!showPassphrase)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--grok-text-muted)] hover:text-[var(--grok-text-primary)]"
                        >
                          {showPassphrase ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {!isSetup && (
                        <p className="mt-1 text-xs text-[var(--grok-text-muted)]">
                          Must contain uppercase, lowercase, numbers, and special characters
                        </p>
                      )}
                    </div>

                    {!isSetup && (
                      <div>
                        <label
                          htmlFor="confirmPassphrase"
                          className="block text-sm font-medium text-[var(--grok-text-primary)] mb-1"
                        >
                          Confirm Passphrase
                        </label>
                        <input
                          id="confirmPassphrase"
                          type={showPassphrase ? 'text' : 'password'}
                          value={confirmPassphrase}
                          onChange={(e) => setConfirmPassphrase(e.target.value)}
                          placeholder="Re-enter passphrase"
                          className="
                            w-full px-3 py-2
                            bg-[var(--grok-surface-2)] border border-[var(--grok-border)]
                            rounded-lg text-[var(--grok-text-primary)]
                            placeholder:text-[var(--grok-text-muted)]
                            focus:outline-none focus:ring-2 focus:ring-[var(--ems-primary-ring)]
                          "
                        />
                      </div>
                    )}

                    {error && (
                      <div className="flex items-center gap-2 p-3 bg-[var(--ems-danger-muted)] text-[var(--ems-danger)] rounded-lg">
                        <AlertCircle size={16} />
                        <span className="text-sm">{error}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      type="button"
                      onClick={onClose}
                      className="
                        flex-1 px-4 py-2
                        bg-[var(--grok-surface-2)] border border-[var(--grok-border)]
                        rounded-lg text-[var(--grok-text-primary)]
                        hover:bg-[var(--grok-hover-bg)]
                        transition-colors
                      "
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isPending || !passphrase}
                      className="
                        flex-1 px-4 py-2
                        bg-[var(--ems-primary)] text-white
                        rounded-lg font-medium
                        hover:bg-[var(--ems-primary-hover)]
                        disabled:opacity-50 disabled:cursor-not-allowed
                        transition-colors
                      "
                    >
                      {isPending ? 'Processing...' : isSetup ? 'Unlock' : 'Create & Unlock'}
                    </button>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
